import withAuth from '@components/utils/withAuth'
import classNames from '@components/utils/classNames'
import { useEffect, useState } from 'react'
import {
  BTN_SUBMIT_REVIEW,
  ERROR_WOOPS_SOMETHING_WENT_WRONG,
  MESSAGE_CHARACTERS_LEFT,
  MIN_BODY_LENGTH_ERROR,
  REVIEW_TITLE,
  REV_SUB,
} from '@components/utils/textVariables'

import {
  EmptyGuid,
  NEXT_CREATE_REVIEW,
  NEXT_REVIEW_CONFIG_API,
  PDP_REVIEW_ACCEPTABLE_IMAGE_MIMES,
  PDP_REVIEW_IMAGE_SIZE_IN_BYTES,
  PDP_REVIEW_NO_OF_IMAGES_ALLOWED,
  ReviewInputPageAction,
} from '@components/utils/constants'
import axios from '@services/axiosInterceptorInstance'
import { useUI } from '@components/ui/context'
import { LoadingDots } from '@components/ui'
import {
  formatBytes,
  resetSubmitData,
  sanitizeBase64,
  submitData,
} from '@framework/utils/app-util'
import SubmitButton from '@components/common/SubmitButton'
import useDataSubmit from '@commerce/utils/use-data-submit'
import { IExtraProps } from '@components/common/Layout/Layout'
import { GA_EVENT } from 'hooks/ga_event.modal'
import { recordGA4Event } from '@components/services/analytics/ga4'
import { useMoEngageEvent } from 'hooks/useMoengageEvent'
import { MO_ENGAGE_EVENT } from 'hooks/moengage_event.modal'
import { Star } from '@components/icons'

declare const window: any

const MIN_LENGTH = 5
const MAX_LENGTH = 240 //@TODO TBD with PO

const TITLE_MAX_LENGTH = 20

interface ReviewInputProps {
  productId: string
  data: any
  setSubmitReview: any
}

const REVIEW_SECTIONS_ENABLED = false

const ReviewInput = ({
  data,
  productId,
  setSubmitReview = null,
  deviceInfo,
}: ReviewInputProps & IExtraProps) => {
  const trackMoEngageEvent = useMoEngageEvent()
  const [rating, setRating] = useState(5)
  const [commentBody, setCommentBody] = useState('')
  const [error, setError] = useState('')
  const [commentTitle, setCommentTitle] = useState('')
  const [submitMessage, setSubmitMessage] = useState('')
  const [datas, setData] = useState({})
  const [allReviewSettings, setAllReviewSettings] = useState<any>()
  const [selectedImages, setSelectedImages] = useState<Array<any>>()
  const { user, setAlert } = useUI()
  const { isMobile, isIPadorTablet } = deviceInfo
  const { state: submitState, dispatch: submitDispatch } = useDataSubmit()

  const handleCommentChange = (e: any) => {
    if (e.target.value.length <= MAX_LENGTH) {
      setCommentBody(e?.target?.value)
    }
  }
  let deviceCheck = ''
  if (isMobile || isIPadorTablet) {
    deviceCheck = 'Mobile'
  } else {
    deviceCheck = 'Desktop'
  }
  const handleRatingChange = (value: number) => setRating(value)
  const handleSubmit = () => {
    recordGA4Event(window, GA_EVENT.RATE_YOUR_EXPERIENCE, {
      ratings: rating,
      attribute_selection: '',
      device: deviceCheck,
      loggedin_status: !!(user?.userId && user?.userId !== EmptyGuid),
    })

    trackMoEngageEvent(
      MO_ENGAGE_EVENT.REVIEW,
      {
        ratings: rating,
        attribute_selection: '',
        review_section: window?.location?.href,
      },
      window
    )

    let selectedValues = new Array<any>()
    let reviewSections: any = {}

    if (REVIEW_SECTIONS_ENABLED) {
      const container: any = document.querySelector('div.rating-sections')
      if (container) {
        const selectedLabels = container.querySelectorAll(
          '.rating-custom-check:checked+.rating-custom-check-label'
        )
        if (selectedLabels?.length) {
          selectedLabels?.forEach((elem: any) => {
            selectedValues.push({
              sectionName: elem?.getAttribute('data-category'),
              options: {
                name: elem?.textContent,
                isChecked: true,
              },
            })
          })
        }
      }

      if (selectedValues?.length) {
        selectedValues.forEach((value) => {
          if (!reviewSections[value?.sectionName]) {
            reviewSections[value?.sectionName] = {
              ...{ options: [value?.options] },
            }
          } else {
            reviewSections[value?.sectionName]['options'] = [
              ...reviewSections[value?.sectionName]['options'],
              ...[value?.options],
            ]
          }
        })
      }
    }

    if (commentBody.length === 0) {
      setError("Review can't be empty")
      return
    }
    if (error.length > 0) return

    const createAsyncReview = async () => {
      submitData(submitDispatch, ReviewInputPageAction.SUBMIT)
      let promises = new Array<Promise<any>>()
      selectedImages?.forEach((blob: any) => {
        promises.push(
          new Promise((resolve, _) => {
            const reader = new FileReader()
            reader.onloadend = () =>
              resolve({
                name: blob?.name,
                data: sanitizeBase64(reader?.result as string),
              })
            reader.readAsDataURL(blob)
          })
        )
      })

      const images = await Promise.all(promises)

      let userName = 'Anonymous'
      if (user?.firstName != null || user?.firstName != '') {
        userName = user?.firstName + ' ' + user?.lastName
      }

      try {
        const { data: submitReviewResult }: any = await axios.post(
          NEXT_CREATE_REVIEW,
          {
            title: commentTitle,
            rating: rating,
            comment: commentBody,
            userId: user?.userId,
            nickname: userName,
            images: images,
            productId,
          }
        )
        if (submitReviewResult?.isValid) {
          setCommentBody('')
          setCommentTitle('')
          setRating(1)
          setData(datas)
          setAlert({ type: 'success', msg: REV_SUB })

          if (submitReviewResult?.message) {
            setSubmitMessage(submitReviewResult?.message)
          } else {
            if (setSubmitReview) {
              if (!(!error && commentBody.length < MIN_LENGTH)) {
                setSubmitReview(false)
              }
            }
          }
        } else {
          setSubmitMessage(ERROR_WOOPS_SOMETHING_WENT_WRONG)
        }
        resetSubmitData(submitDispatch)
      } catch (error) {
        setSubmitMessage(ERROR_WOOPS_SOMETHING_WENT_WRONG)
      }
    }
    createAsyncReview()
  }

  const handleCommentTitle = (e: any) => {
    if (e.target.value.length <= TITLE_MAX_LENGTH) {
      setCommentTitle(e.target.value)
    }
  }

  const handleCommentBody = (e: any) => {
    if (e.target.value.length < MIN_LENGTH) setError(MIN_BODY_LENGTH_ERROR)
    if (e.target.value.length >= MIN_LENGTH) setError('')
    setCommentBody(e.target.value)
  }

  const uploadReviewPics = (ev: any) => {
    const target = ev?.target
    if (target) {
      const files = target?.files
      if (files?.length) {
        const allowedFileExtns = PDP_REVIEW_ACCEPTABLE_IMAGE_MIMES?.split(
          ','
        )?.map((x) => x?.toLowerCase()?.trim())
        const allowedFiles = Array.from(files)?.filter(
          (file: any) =>
            allowedFileExtns?.includes(
              file?.name.substring(file?.name.lastIndexOf('.'))?.toLowerCase()
            ) && file.size <= PDP_REVIEW_IMAGE_SIZE_IN_BYTES
        )
        if (allowedFiles?.length) {
          let filesToUpload =
            allowedFiles?.length > 5
              ? allowedFiles?.slice(0, PDP_REVIEW_NO_OF_IMAGES_ALLOWED)
              : allowedFiles
          filesToUpload = (selectedImages ?? []).concat(allowedFiles)
          filesToUpload =
            filesToUpload?.length > 5
              ? filesToUpload?.slice(0, PDP_REVIEW_NO_OF_IMAGES_ALLOWED)
              : filesToUpload
          setSelectedImages(filesToUpload)
        }
      }
    }
  }

  useEffect(() => {
    const handleAsync = async () => {
      const { data: reviewSettings }: any = await axios.post(
        NEXT_REVIEW_CONFIG_API
      )
      setAllReviewSettings(reviewSettings)
    }

    if (REVIEW_SECTIONS_ENABLED) {
      handleAsync()
    }
  }, [])

  return (
    <>
      <div className="flex flex-col px-6 mt-4">
        <h4 className="mb-1 text-lg font-semibold dark:text-black">
          Rate this Item
        </h4>
        <p className="text-sm dark:text-black">{data?.name}</p>
      </div>
      <div className="flex flex-col">
        {submitMessage ? (
          <div>
            <span className="text-2xl text-gray-900 dark:text-gray-900">
              {submitMessage}
            </span>
          </div>
        ) : (
          <>
            <div className="w-full px-6">
              <div className="flex gap-1 mt-2 mb-1 sm:mt-5 sm:mb-3 sm:gap-8 flex-center">
                {Array.from(Array(6).keys()).map(
                  (num) =>
                    num > 0 && (
                      <Star
                        key={`starIcon-${num}`}
                        className={classNames(
                          rating >= num ? 'text-orange-500' : 'text-gray-200',
                          'sm:h-12 sm:w-12 h-8 w-8 flex-shrink-0'
                        )}
                        onClick={() => handleRatingChange(num)}
                        aria-hidden="true"
                      />
                    )
                )}
              </div>
              <div className="flex flex-col w-full">
                {rating > 3 ? (
                  <>
                    <p className="mb-4 text-sm dark:text-gray-700">
                      Wohoo! You've rated us your deilvery experience with{' '}
                      {rating} stars. Tell us what went well for you?
                    </p>
                  </>
                ) : (
                  <>
                    <p className="mb-4 text-sm dark:text-gray-700">
                      Ohooo! You've rated us your deilvery experience with{' '}
                      {rating} stars. Tell us what went wrong for you?
                    </p>
                  </>
                )}
              </div>

              {REVIEW_SECTIONS_ENABLED ? (
                <>
                  {!allReviewSettings ? (
                    <LoadingDots />
                  ) : (
                    <>
                      {allReviewSettings?.length && (
                        <div className="w-full rating-sections">
                          {allReviewSettings?.map(
                            (setting: any, sid: number) => (
                              <>
                                <div
                                  className="flex flex-col w-full pb-2 mb-3 border-b border-gray-200 border-dashed"
                                  key={`review-${sid}-section`}
                                >
                                  <h3 className="font-semibold text-black text-md">
                                    {setting?.SectionName}
                                  </h3>
                                  <input
                                    type="hidden"
                                    value={setting?.SectionName}
                                  />
                                </div>
                                {setting?.Options?.length > 0 && (
                                  <div className="w-full">
                                    {setting?.Options.map(
                                      (option: any, oid: number) => (
                                        <>
                                          <div
                                            className="inline-block mb-3 mr-3 sm:mr-2"
                                            key={`review-${oid}-form`}
                                          >
                                            <input
                                              className="rating-custom-check rating-section-option"
                                              id={`${
                                                setting?.SectionName
                                              }_${option.Name.replace(
                                                ' ',
                                                ''
                                              ).toLowerCase()}`}
                                              value={option?.Name}
                                              type="checkbox"
                                            />

                                            <label
                                              className="px-4 text-xs rating-custom-check-label"
                                              htmlFor={`${
                                                setting?.SectionName
                                              }_${option.Name.replace(
                                                ' ',
                                                ''
                                              ).toLowerCase()}`}
                                              title={option.Name}
                                              data-category={
                                                setting?.SectionName
                                              }
                                            >
                                              {option.Name}
                                            </label>
                                          </div>
                                        </>
                                      )
                                    )}
                                  </div>
                                )}
                              </>
                            )
                          )}
                        </div>
                      )}
                    </>
                  )}
                </>
              ) : null}

              <div className="flex flex-col w-full pt-2 border-t border-gray-200 border-dashed">
                <h4 className="text-base font-bold text-black dark:text-black">
                  Write a review to help fellow shoppers
                </h4>
              </div>

              <div className="floating-label">
                <input
                  placeholder={REVIEW_TITLE}
                  onChange={handleCommentTitle}
                  value={commentTitle}
                  className="relative w-full min-w-0 px-4 py-2 mt-2 mb-2 text-gray-900 placeholder-gray-500 bg-white border border-gray-300 rounded-sm shadow-sm appearance-none dark:text-black focus:outline-none focus:border-black focus:ring-1 focus:ring-black"
                />
                <label className="text-sm text-gray-700 dark:text-black">
                  {REVIEW_TITLE}
                </label>
              </div>

              <div className="w-full pb-4 mb-6">
                <textarea
                  className="w-full px-4 py-3 text-sm border textarea-input dark:text-black"
                  placeholder="Any other thoughts?"
                  value={commentBody}
                  onChange={handleCommentBody}
                  required
                  onKeyPress={(ev: any) => {
                    const target = ev?.target
                    if (target) {
                      if (target?.value?.length > MAX_LENGTH) {
                        target.value = target?.value?.substring(0, MAX_LENGTH)
                        ev.preventDefault()
                        ev.stopPropagation()
                      }
                      handleCommentChange(ev)
                    }
                  }}
                  // onPaste={(ev: any) => {
                  //   ev.preventDefault();
                  //   ev.stopPropagation();
                  // }}
                ></textarea>
                <span className="float-right text-sm text-gray-900">
                  {MESSAGE_CHARACTERS_LEFT}: {MAX_LENGTH - commentBody.length}
                </span>
              </div>
            </div>

            <div className="w-full px-6 pb-10">
              <h4 className="text-base font-bold text-black dark:text-black">
                Add upto 5 pictures{' '}
              </h4>
              <p>
                (Images exceeding {formatBytes(PDP_REVIEW_IMAGE_SIZE_IN_BYTES)}{' '}
                will be ignored)
              </p>
              <div className="my-3">
                <input
                  type="file"
                  id="myfile"
                  name="myfile"
                  multiple
                  accept={PDP_REVIEW_ACCEPTABLE_IMAGE_MIMES}
                  onChange={(ev: any) => uploadReviewPics(ev)}
                />
                <label className="w-5 h-5" htmlFor="myfile">
                  <i className="sprite-icon sprite-file-plus"></i>
                </label>
              </div>

              {selectedImages?.length &&
                selectedImages?.map((file: any, idx: number) => (
                  <img
                    alt="Review Customer"
                    width={50}
                    height={50}
                    key={`review-${idx}-image`}
                    src={URL.createObjectURL(file)}
                  />
                ))}
            </div>

            <div className="sticky bottom-0 flex flex-col w-full px-6 py-4 bg-white border-t border-gray-200">
              <div className="flex flex-col w-full">
                {error.length > 0 && (
                  <span className="pb-4 text-sm text-center text-red-900">
                    {error}
                  </span>
                )}
              </div>
              <SubmitButton
                cssClass="w-full min-height-btn-r flex items-center justify-center px-4 py-3 -mr-0.5 text-white bg-black border-2 border-black rounded-sm hover:bg-gray-800 hover:text-whitesm:px-6 hover:border-gray-900"
                submitState={submitState}
                source={ReviewInputPageAction.SUBMIT}
                onClick={() => handleSubmit()}
              >
                {BTN_SUBMIT_REVIEW}
              </SubmitButton>
            </div>
          </>
        )}
      </div>
    </>
  )
}

export default withAuth(ReviewInput, false)
